/**
 * Menu helpers — ported from ThemeMaker's
 * {@code lms-portal/src/lib/platform/menu.ts}.
 *
 * <p>{@code header.menu} is heterogeneous — entries can be links,
 * groups, or registered components (see {@link MenuEntry}). Sidebar
 * styles walk the hierarchy directly; top-bar styles can't (horizontal
 * nav has no place to render a group label), so they call
 * {@link flattenMenu} and iterate the flat list.
 *
 * <p>NOTE: flattening does NOT apply permission filtering. Apply
 * {@code filterVisible} AFTER flattening — perms depend on runtime
 * {@code currentUser}; flattening is purely structural.
 */

import type { MenuComponentEntry, MenuEntry, MenuLink } from './menuTypes';

/** Sibling fields on {@link MenuComponentEntry} that get folded into the
 *  merged props bag passed to the resolved component, so implementations
 *  only ever need to read from one place ({@code props}). Keep this list
 *  in sync with new {@code categoriesMenu}-specific (or future
 *  component-specific) fields added to {@link MenuComponentEntry}. */
const MIRRORED_ENTRY_FIELDS = [
  'label',
  'href',
  'icon',
  'allCategoriesLabel',
  'showCategoryIcons',
  'defaultIcon',
  'backgroundColor',
  'hoverTextColor',
  'menuWidth',
  'allCategoriesIcon',
  'limit',
  'seeAllLabel',
  'seeAllHref',
  'seeAllIcon',
  'viewAllLabel',
  'viewAllHref',
  'viewAllIcon',
] as const satisfies readonly (keyof MenuComponentEntry)[];

/**
 * Builds the props bag a {@code kind: 'component'} menu entry's resolved
 * component receives: {@code entry.props} plus every field in
 * {@link MIRRORED_ENTRY_FIELDS} that's actually set on the entry itself
 * (operator-authored sibling fields, not nested under {@code props}).
 */
export function mergeMenuComponentProps(entry: MenuComponentEntry): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...(entry.props ?? {}) };
  for (const field of MIRRORED_ENTRY_FIELDS) {
    const value = entry[field];
    if (value !== undefined) merged[field] = value;
  }
  return merged;
}

/**
 * Flatten a heterogeneous menu into plain links. Group labels are
 * dropped; group {@code links} are spliced inline. Component entries
 * are DROPPED — use {@link flattenMenuWithComponents} when the
 * renderer can mount registered components inline.
 */
export function flattenMenu(menu: MenuEntry[] | undefined | null): MenuLink[] {
  if (!menu) return [];
  const out: MenuLink[] = [];
  for (const entry of menu) {
    if (entry.kind === 'group') {
      for (const link of entry.links ?? []) out.push(link);
      continue;
    }
    if (entry.kind === 'component') continue;
    out.push(entry as MenuLink);
  }
  return out;
}

/**
 * Like {@link flattenMenu}, but preserves {@code kind: 'component'}
 * entries. Caller is responsible for dispatching to the registry.
 */
export function flattenMenuWithComponents(menu: MenuEntry[] | undefined | null): MenuEntry[] {
  if (!menu) return [];
  const out: MenuEntry[] = [];
  for (const entry of menu) {
    if (entry.kind === 'group') {
      for (const link of entry.links ?? []) out.push(link);
      continue;
    }
    if (entry.kind === 'component') {
      out.push(entry);
      continue;
    }
    const link = entry as MenuLink;
    if (!link.kind && (!link.href || link.href.trim() === '')) continue;
    out.push(link);
  }
  return out;
}
